/**
 * Module: lib/uploadTelemetry
 * Role: Shared upload-funnel telemetry accumulator (FTR-1317).
 *       Replaces 5 copy-paste beacon calls across photo bricks.
 *
 * Uses: api/client:apiFetch, lib/abortTimeout:timeoutSignal
 * Used by: ScreenshotBrick, MultiImageUploadBrick, UrlInputBrick,
 *          CartScreen, UgcPhotoGridScreen, VideoUploadBrick (TECH-1356),
 *          pantene/VideoUpload (TECH-1356)
 */

import { apiFetch } from '../api/client';
import { timeoutSignal } from './abortTimeout';

// SYMMETRIC LIMIT — sync with:
//   - src/api/v2/buyer/flow.py UPLOAD_MAX_STAGES (server validation, 24)
//   - src/cli/_flow_alerting_rules.py UPLOAD_HANG_THRESHOLD
export const UPLOAD_MAX_STAGES = 24;

// Timeout constants for upload pipeline steps
export const RESIZE_DECODE_TIMEOUT_MS = 20_000;
export const PRESIGN_TIMEOUT_MS = 10_000;
export const SUBMIT_TIMEOUT_MS = 30_000;
export const BEACON_TIMEOUT_MS = 5_000;

/** Adaptive upload timeout: at least 120s, or file.size/50 ms (whichever is larger). */
export function uploadTimeoutFor(sizeBytes: number): number {
  return Math.max(120_000, Math.round(sizeBytes / 50));
}

export type UploadOutcome = 'success' | 'failed' | 'timeout' | 'abandoned';

interface Stage {
  stage: string;
  elapsed_ms: number;
  meta?: Record<string, unknown>;
}

interface UploadFunnel {
  /** Record a funnel stage. Call in order during the upload pipeline. */
  stage: (name: string, meta?: Record<string, unknown>) => void;
  /** Flush the funnel to the server. Idempotent — second call is a no-op. */
  flush: (outcome: UploadOutcome) => void;
  /** Unique attempt ID for correlation. */
  attemptId: string;
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Create an upload funnel telemetry accumulator.
 *
 * Usage:
 *   const funnel = createUploadFunnel(slotId, checkpointSlug);
 *   funnel.stage('file_picked', { mime: file.type, size: file.size });
 *   funnel.stage('resize_done');
 *   ... pipeline steps ...
 *   funnel.flush('success');
 *
 * On error/timeout: flush('failed') or flush('timeout') — triggers immediate POST.
 * PII-free: meta carries mime/size/dims/http_status — NEVER filename or signed URL.
 */
export function createUploadFunnel(
  slotId: string,
  checkpointSlug: string,
  mediaKind: 'photo' | 'video' = 'photo',
): UploadFunnel {
  const attemptId = generateId();
  const startTime = Date.now();
  const stages: Stage[] = [];
  let flushed = false;

  const buildVersion: string | null =
    import.meta.env?.VITE_COMMIT_SHA ?? null;

  const doFlush = (outcome: UploadOutcome): void => {
    if (flushed) return;
    flushed = true;

    // Remove pagehide listener if registered
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }

    // FTR-1317 follow-up: this flush frequently fires while the webview is being
    // backgrounded / navigated away (see the visibilitychange 'hidden' handler
    // below), which drops an ordinary fetch and loses the beacon. `keepalive: true`
    // lets the request outlive the page so the telemetry actually reaches the server.
    //
    // Why not navigator.sendBeacon: the upload-error endpoint is auth-gated
    // (get_current_buyer requires an `Authorization: Bearer <jwt>` header) and
    // sendBeacon cannot set request headers — a beacon would arrive unauthenticated,
    // 401, and silently drop the row (worse than today). apiFetch attaches the bearer
    // token and a keepalive fetch survives backgrounding just as well, so the exact
    // same payload/contract is preserved while gaining survivability.
    void apiFetch(`/api/v2/buyer/slots/${slotId}/upload-error`, {
      method: 'POST',
      keepalive: true,
      body: JSON.stringify({
        stages: stages.slice(0, UPLOAD_MAX_STAGES),
        outcome,
        checkpoint_slug: checkpointSlug,
        upload_attempt_id: attemptId,
        build_version: buildVersion,
        media_kind: mediaKind,  // TECH-1356: video-awareness for rule8 alerting
      }),
      signal: timeoutSignal(BEACON_TIMEOUT_MS),
    }).catch(() => {
      // Best-effort — never block the UI
    });
  };

  // Auto-flush 'abandoned' when user navigates away mid-upload
  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden' && !flushed) {
      doFlush('abandoned');
    }
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  return {
    attemptId,
    stage(name: string, meta?: Record<string, unknown>): void {
      if (flushed) return;
      stages.push({
        stage: name,
        elapsed_ms: Date.now() - startTime,
        ...(meta ? { meta } : {}),
      });
    },
    flush: doFlush,
  };
}
